import { computed, unref } from '@vue/composition-api';

/**
 * Normalizes `matched_clusters` (same shape as saved-search `matched_searches`: id + display name).
 *
 * @param {Object} item - single competitor_activity row from API
 * @returns {Array<{ cluster_id: number|null, cluster_name: string }>}
 */
export function normalizeMatchedClusters(item) {
  if (Array.isArray(item?.matched_clusters) && item.matched_clusters.length > 0) {
    return item.matched_clusters.map((c) => ({
      cluster_id: c?.cluster_id ?? null,
      cluster_name: c?.cluster_name ?? ''
    }));
  }
  if (item?.cluster_id != null || item?.cluster_name) {
    return [{ cluster_id: item.cluster_id ?? null, cluster_name: item.cluster_name || '' }];
  }
  return [];
}

/**
 * Normalizes `competitor_activity` for the UI.
 * Ensures `matched_clusters` is always an array (API order preserved).
 *
 * @param {Array} raw - competitor_activity from daily digest API
 * @returns {Array<Object>}
 */
export function normalizeCompetitorActivity(raw) {
  if (!Array.isArray(raw) || raw.length === 0) return [];
  return raw.map((item) => ({
    ...item,
    matched_clusters: normalizeMatchedClusters(item)
  }));
}

/**
 * Composable for competitor activity (recent wins from tracked competitors).
 * Uses the shared Daily Digest instance to derive `competitor_activity` data.
 *
 * @param {Object} params - Configuration
 * @param {Object} params.dailyDigest - dailyDigest ref from useDailyDigest
 * @param {Object} params.loading - loading ref from useDailyDigest
 * @returns {Object} Reactive state for the competitor activity section
 */
export function useCompetitorActivity({ dailyDigest, loading }) {
  const competitorActivity = computed(() => {
    const digest = unref(dailyDigest);
    if (!digest || !digest.competitor_activity) return [];
    return normalizeCompetitorActivity(digest.competitor_activity);
  });

  const loadingCompetitorActivity = loading;

  return {
    competitorActivity,
    loading: loadingCompetitorActivity
  };
}
